/** Where the time of the last reload for a missing chunk is kept, for this tab only. */
export const reloadedAtKey = 'paige.chunk-reload-at';

/** A second missing chunk this soon after reloading for one is a real failure, not a deploy. */
export const reloadWindow = 60_000;

export interface ChunkReloadOptions {
  held: () => string | null;
  reload: () => void;
  /** Whether the router is moving to a screen (a preload on hover is not). */
  navigating: () => boolean;
  /** Asked instead of reloading: is a newer Paige really deployed? (the banner says so if it is) */
  confirmUpdate: () => void;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  now?: () => number;
}

/**
 * A screen's code is fetched when the screen is first opened. After a deploy the old names are
 * gone, so a tab on the old Paige asks for a file that is not there and Vite raises
 * `vite:preloadError`. That is the deploy, not a broken screen: the page is loaded once more, onto
 * the new Paige, at the address the person was going to (the router has already moved there).
 *
 * Once only: if the reload is followed within a minute by another missing chunk, the event is left
 * alone and the screen's error boundary says it could not be opened (ScreenFailure), rather than
 * reloading in a loop. Only while moving to a screen: a screen fetched ahead of time because the
 * pointer rests on its link is not a reason to reload the screen the person is reading. Never while
 * the page is held (reload-hold.ts): the event is cancelled then too, because the router's lazy
 * screens reload by themselves on a module that cannot be found, with no regard for held work; the
 * screen says it could not be opened instead and the work in the page goes on. Whenever it does not
 * reload, `confirmUpdate` asks which Paige is deployed, so the banner says Paige was updated if it
 * was (a chunk lost to the network is not an update). Returns the way to stop.
 */
export function reloadOnMissingChunk(
  options: ChunkReloadOptions,
  win: Window = window,
): () => void {
  const handle = (event: Event) => {
    if (options.held() !== null) {
      event.preventDefault();
      options.confirmUpdate();
      return;
    }
    if (!options.navigating() || !claimReload(options)) {
      options.confirmUpdate();
      return;
    }
    event.preventDefault();
    options.reload();
  };
  win.addEventListener('vite:preloadError', handle);
  return () => win.removeEventListener('vite:preloadError', handle);
}

/** Whether this tab may reload for a missing chunk now; records that it did. */
function claimReload(options: ChunkReloadOptions): boolean {
  const now = (options.now ?? Date.now)();
  try {
    const storage = options.storage ?? window.sessionStorage;
    const last = Number(storage.getItem(reloadedAtKey));
    if (Number.isFinite(last) && last > 0 && now - last < reloadWindow) return false;
    storage.setItem(reloadedAtKey, String(now));
    return true;
  } catch {
    // Without storage there is no way to know a reload already happened; do not risk a loop.
    return false;
  }
}
